import './style.scss'
import { Button, Modal, Popover, Radio, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Api from '@/api/account'
import * as homeApi from '@/api/home'
import Code from '@/assets/code.webp'
import IconChecked from '@/assets/icon-checked.png'
import RedPackage from '@/assets/red-package.gif'
import { IProduct, UserStore } from '@/global-states'
import { eventTracking } from '@/libs/util'
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import CheckLogin from '../CheckLogin'
import ContractModal from '../ContractModal'
import PayModal from '../PayModal'

export interface IProductList {
  base: IProduct[]
  high: IProduct[]
}

interface IProps {
  onCancel?: () => void
}

const PlanProduct = forwardRef((props: IProps, ref) => {
  const { t: translate } = useTranslation(['plan_modal'])
  const { onCancel } = props
  const { userPackage, payStatus } = UserStore
  const [type, setType] = useState<'month' | 'year'>('month')
  const [tabIndex, setTabIndex] = useState(0)
  const [payModalOpen, setPayModalOpen] = useState(false)
  const [showProducts, setShowProducts] = useState<IProduct[]>([])
  const [payProduct, setPayProduct] = useState<IProduct>()
  const [contractModalOpen, setContractModalOpen] = useState(false)
  const [coupon, setCoupon] = useState<any>()
  const [countdown, setCountdown] = useState<any[]>([])

  const baseProduct = useMemo(() => {
    return showProducts?.find((p) => p?.level === 10 && (type === 'month' ? p?.units === 1 : p?.units === 12))
  }, [showProducts, type])

  const highProduct = useMemo(() => {
    return showProducts?.find((p) => p?.level === 20 && (type === 'month' ? p?.units === 1 : p?.units === 12))
  }, [showProducts, type])

  const blackVipProduct = useMemo(() => {
    return showProducts?.find((p) => p?.level === 50 && (type === 'month' ? p?.units === 1 : p?.units === 12))
  }, [showProducts, type])

  const companyProduct = useMemo(() => {
    return showProducts?.find((p) => p?.level === 100 && (type === 'month' ? p?.units === 1 : p?.units === 12))
  }, [showProducts, type])

  useImperativeHandle(ref, () => ({
    update: () => {
      getProductList()
      getClaimedCoupons()
    }
  }))

  useEffect(() => {
    getProductList()
  }, [])

  useEffect(() => {
    if (!coupon?.valid_window_seconds) {
      setCountdown([])
      return
    }

    const endTime = Date.now() + coupon.valid_window_seconds * 1000

    const updateCountdown = () => {
      const now = Date.now()
      const remaining = Math.max(0, endTime - now)

      if (remaining <= 0) {
        clearInterval(timer)
        getClaimedCoupons()
        return
      }

      const days = Math.floor(remaining / (1000 * 60 * 60 * 24))
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000)

      const formatted = [
        days.toString(),
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0')
      ]

      setCountdown(formatted)
    }

    updateCountdown()
    const timer = setInterval(updateCountdown, 1000)

    return () => clearInterval(timer)
  }, [coupon])

  const getProductList = async () => {
    const { list }: { list: IProduct[] } = await Api.getProductList()

    const showProducts = [
      list.find((l) => l.level === 10 && l.units === 1),
      list.find((l) => l.level === 10 && l.units === 12),
      list.find((l) => l.level === 20 && l.units === 1),
      list.find((l) => l.level === 20 && l.units === 12),
      list.find((l) => l.level === 50 && l.units === 1),
      list.find((l) => l.level === 100 && l.units === 1),
      list.find((l) => l.level === 100 && l.units === 12)
    ]
    setShowProducts(showProducts as IProduct[])

    const vipProduct = list.find((l) => l.level === 50 && l.units === 1)

    if (vipProduct && vipProduct.sale_opening_time) {
      const time = vipProduct.sale_opening_time * 1000 - Date.now()
      if (time > 0) {
        setTimeout(() => {
          getProductList()
        }, time)
      }
    }
  }

  const getClaimedCoupons = async () => {
    const res = await homeApi.getClaimedCoupons({
      status: 2
    })
    const maxItem = res?.list?.reduce((max: any, item: any) => (item.deduct_amount > max.deduct_amount ? item : max))

    if (maxItem) {
      setCoupon(maxItem)
    } else {
      setCoupon(undefined)
    }
  }

  const upgradeTip = (e: any) => {
    e.stopPropagation()
    Modal.warn({
      title: translate('plan_modal.upgrade_rules_title'),
      content: (
        <div className="declare-list" style={{ marginLeft: -20 }}>
          <ul style={{ paddingLeft: 10 }}>
            <li style={{ padding: '8px 0' }}>{translate('plan_modal.upgrade_rule_1')}</li>
            <li style={{ padding: '8px 0' }}>{translate('plan_modal.upgrade_rule_2')}</li>
            <li style={{ padding: '8px 0' }}>{translate('plan_modal.upgrade_rule_3')}</li>
          </ul>
        </div>
      ),
      okText: translate('plan_modal.got_it')
    })
  }

  const onSub = (product?: IProduct) => {
    onCancel?.()
    setPayProduct(product)
    setPayModalOpen(true)
  }

  const onSubCompany = async () => {
    const res = await homeApi.checkContract()
    onCancel?.()
    setPayProduct(companyProduct)
    if (res?.require_new_contract) {
      setContractModalOpen(true)
    } else {
      setPayModalOpen(true)
    }
  }

  const getOriginPrice = (product?: IProduct) => {
    if (product) {
      const { discounted_unit_price, original_unit_price, units } = product
      const originPrice = original_unit_price * units
      const discountPrice = Math.max(0, discounted_unit_price * units - (coupon?.deduct_amount || 0))

      if (discountPrice < originPrice) {
        return (
          <div className="origin-price">
            <label className="num">¥{((product?.original_unit_price || 0) * (product?.units || 0)) / 100}</label>
          </div>
        )
      }
    }
    return null
  }

  const getDiscountPrice = (product?: IProduct) => {
    if (product) {
      const { discounted_unit_price, units } = product
      const couponAmount = coupon?.deduct_amount || 0
      return `¥${Math.max(0, discounted_unit_price * units - couponAmount) / 100}`
    }
  }

  return (
    <>
      <div className="plan-content-main" style={{ opacity: showProducts?.length ? 1 : 0 }}>
        <Radio.Group
          value={tabIndex}
          onChange={(e) => {
            const index = e.target.value
            setTabIndex(index)
          }}
          buttonStyle="solid"
        >
          <Radio.Button value={0}>{translate('plan_modal.personal_edition')}</Radio.Button>
          <Radio.Button value={1}>{translate('plan_modal.enterprise_edition')}</Radio.Button>
        </Radio.Group>

        <Radio.Group
          value={type}
          onChange={(e) => {
            const type: 'month' | 'year' = e.target.value
            setType(type)
          }}
        >
          <Radio value={'month'}>{translate('plan_modal.monthly_member')}</Radio>
          <Radio value={'year'}>{translate('plan_modal.yearly_member')}</Radio>
        </Radio.Group>

        {!!countdown?.length && (
          <div className="count-time">
            <img className="gif" src={RedPackage} />
            <label className="red">
              {(coupon?.deduct_amount || 0) / 100}
              {translate('plan_modal.coupon_amount')}
            </label>
            <label>{translate('plan_modal.expiring_soon')}</label>
            <span className="time">
              <label className="box">{countdown[0]}</label>
              <label className="unit">{translate('plan_modal.day')}</label>
              <label className="box">{countdown[1]}</label>
              <label className="unit">:</label>
              <label className="box">{countdown[2]}</label>
              <label className="unit">:</label>
              <label className="box">{countdown[3]}</label>
            </span>
          </div>
        )}

        {tabIndex === 0 ? (
          <div className="plan-list">
            <div className="list base">
              <div className="title">{translate('plan_modal.basic_edition')}</div>
              <div className="price">
                <div className="discount-price">
                  <label className="num">{getDiscountPrice(baseProduct)}</label>
                  <label className="unit">
                    /{type === 'month' ? translate('plan_modal.month') : translate('plan_modal.year')}
                  </label>
                </div>
                {getOriginPrice(baseProduct)}
              </div>

              <div className="vip-desc">
                {translate('plan_modal.includes_points', {
                  points: (highProduct?.unit_credits || 0) * (highProduct?.units || 0),
                  minutes: type === 'year' ? 360 : 30
                })}
              </div>

              {baseProduct?.status === 1 && (
                <CheckLogin onLoginSuccess={getProductList}>
                  <Button
                    type="primary"
                    onClick={() => {
                      eventTracking('BuyNow')
                      onSub(baseProduct)
                    }}
                  >
                    {translate('plan_modal.buy_now')}
                  </Button>
                </CheckLogin>
              )}
              {baseProduct?.status === 2 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('UpgradeNow')
                    onSub(baseProduct)
                  }}
                >
                  {translate('plan_modal.upgrade_now')}
                  <div className="tips" onClick={upgradeTip}>
                    <label>{translate('plan_modal.upgrade_rules')}</label>
                    <InfoCircleOutlined />
                  </div>
                </Button>
              )}
              {baseProduct?.status === 3 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('RenewNow')
                    onSub(baseProduct)
                  }}
                >
                  {translate('plan_modal.renew_now')}
                </Button>
              )}
              {baseProduct?.status === 4 && (
                <Button disabled type="default">
                  {translate('plan_modal.no_downgrade')}
                </Button>
              )}

              <div className="vip-rights">
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.watermark_removal')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.avatar_cloning')}</div>
                    <div className="desc gradient">{translate('plan_modal.monthly_100_times')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.digital_creation')}</div>
                    <div className="desc gradient">{translate('plan_modal.10_points_per_second')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.output_resolution')}</div>
                    <div className="desc gradient">{translate('plan_modal.up_to_1080p')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.voice_cloning')}</div>
                    <div className="desc gradient">{translate('plan_modal.basic_edition')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.points_recharge')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.generation_speed')}</div>
                    <div className="desc gradient">{translate('plan_modal.moderate')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
              </div>
            </div>

            <div className="list actived">
              <div className="list-tag">
                <label className="gradient">{translate('plan_modal.most_popular')}</label>
              </div>
              <div className="title">{translate('plan_modal.premium_edition')}</div>
              <div className="price">
                <div className="discount-price">
                  <label className="num">{getDiscountPrice(highProduct)}</label>
                  <label className="unit">
                    /{type === 'month' ? translate('plan_modal.month') : translate('plan_modal.year')}
                  </label>
                </div>
                {getOriginPrice(highProduct)}
              </div>
              <div className="vip-desc">
                {translate('plan_modal.includes_points', {
                  points: (highProduct?.unit_credits || 0) * (highProduct?.units || 0),
                  minutes: type === 'year' ? 360 : 30
                })}
              </div>

              {highProduct?.status === 1 && (
                <CheckLogin onLoginSuccess={getProductList}>
                  <Button
                    type="primary"
                    onClick={() => {
                      eventTracking('BuyNow')
                      onSub(highProduct)
                    }}
                  >
                    {translate('plan_modal.buy_now')}
                  </Button>
                </CheckLogin>
              )}
              {highProduct?.status === 2 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('UpgradeNow')
                    onSub(highProduct)
                  }}
                >
                  {translate('plan_modal.upgrade_now')}
                  <div className="tips" onClick={upgradeTip}>
                    <label>{translate('plan_modal.upgrade_rules')}</label>
                    <InfoCircleOutlined />
                  </div>
                </Button>
              )}
              {highProduct?.status === 3 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('RenewNow')
                    onSub(highProduct)
                  }}
                >
                  {translate('plan_modal.renew_now')}
                </Button>
              )}
              {highProduct?.status === 4 && (
                <Button disabled type="default">
                  {translate('plan_modal.no_downgrade')}
                </Button>
              )}

              {highProduct?.status === 5 && (
                <Button disabled type="default">
                  {translate('plan_modal.sold_out')}
                </Button>
              )}

              <div className="vip-rights">
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.includes_all_basic_rights')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.avatar_cloning')}</div>
                    <div className="desc gradient">{translate('plan_modal.unlimited_times')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.digital_creation')}</div>
                    <div className="desc gradient">
                      <span
                        onClick={() => {
                          window.open('https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0')
                        }}
                        className="border gradient"
                      >
                        {translate('plan_modal.9_points_per_second')}
                      </span>
                    </div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.output_resolution')}</div>
                    <div className="desc gradient">{translate('plan_modal.up_to_4k')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.voice_cloning')}</div>
                    <div className="desc gradient">
                      {translate('plan_modal.high_fidelity')}
                      <Popover
                        content={
                          <div className="common-popover w100">
                            <div>
                              {translate('plan_modal.voice_cloning_description')}
                              <strong>{translate('plan_modal.premium_voice_note')}</strong>
                              {translate('plan_modal.view_document')}
                              <a
                                target="_blank"
                                href="https://lingverse.feishu.cn/docx/U6qiddhObonp56x3w1ucAlJ6nqb"
                                rel="noreferrer"
                                style={{ paddingLeft: 2, textDecoration: 'underline' }}
                              >
                                {translate('plan_modal.voice_cloning_doc')}
                              </a>
                            </div>
                          </div>
                        }
                        placement="top"
                      >
                        <ExclamationCircleOutlined />
                      </Popover>
                    </div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.api_access')}</div>
                    <div className="desc">
                      <span
                        onClick={() => {
                          window.open('https://api.hifly.cc/hifly.html')
                        }}
                        className="border gradient"
                      >
                        {translate('plan_modal.api_document')}
                      </span>
                    </div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.generation_speed')}</div>
                    <div className="desc gradient">{translate('plan_modal.fast')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
              </div>
            </div>
            {blackVipProduct && (
              <div className="list">
                <div className="list-tag black">
                  <label>{translate('plan_modal.cost_effective')}</label>
                </div>
                <div className="title">{translate('plan_modal.black_edition')}</div>
                <div className="price">
                  <div className="discount-price">
                    <label className="num">{getDiscountPrice(blackVipProduct)}</label>
                    <label className="unit">
                      /{type === 'month' ? translate('plan_modal.month') : translate('plan_modal.year')}
                    </label>
                  </div>
                  {getOriginPrice(blackVipProduct)}
                </div>
                <div className="vip-desc">
                  {translate('plan_modal.includes_points', {
                    points: (blackVipProduct?.unit_credits || 0) * (blackVipProduct?.units || 0),
                    minutes: type === 'year' ? 360 : 30
                  })}
                </div>

                {blackVipProduct?.status === 1 && (
                  <CheckLogin onLoginSuccess={getProductList}>
                    <Button
                      type="primary"
                      onClick={() => {
                        eventTracking('BuyNow')
                        onSub(blackVipProduct)
                      }}
                    >
                      {translate('plan_modal.buy_now')}
                    </Button>
                  </CheckLogin>
                )}
                {blackVipProduct?.status === 2 && (
                  <Button
                    type="primary"
                    onClick={() => {
                      eventTracking('UpgradeNow')
                      onSub(blackVipProduct)
                    }}
                  >
                    {translate('plan_modal.upgrade_now')}
                    <div className="tips" onClick={upgradeTip}>
                      <label>{translate('plan_modal.upgrade_rules')}</label>
                      <InfoCircleOutlined />
                    </div>
                  </Button>
                )}
                {blackVipProduct?.status === 3 && (
                  <Button
                    type="primary"
                    onClick={() => {
                      eventTracking('RenewNow')
                      onSub(blackVipProduct)
                    }}
                  >
                    {translate('plan_modal.renew_now')}
                  </Button>
                )}
                {blackVipProduct?.status === 4 && (
                  <Button disabled type="default">
                    {translate('plan_modal.no_downgrade')}
                  </Button>
                )}

                {blackVipProduct?.status === 5 && (
                  <Button disabled type="default">
                    {blackVipProduct.sale_opening_time
                      ? `${dayjs(blackVipProduct.sale_opening_time * 1000).format('YYYY-MM-DD HH:mm')}${translate(
                          'plan_modal.on_sale'
                        )}`
                      : translate('plan_modal.sold_out')}
                    <span className="tag">
                      {userPackage?.current_membership_level === 50
                        ? translate('plan_modal.limited_sale')
                        : translate('plan_modal.stay_tuned')}
                    </span>
                    {blackVipProduct.sale_opening_time && <span className="time"></span>}
                  </Button>
                )}

                <div className="vip-rights">
                  <div className="rights-item">
                    <div className="left">
                      <div className="title">{translate('plan_modal.includes_all_premium_rights')}</div>
                    </div>
                    <div className="right">
                      <img src={IconChecked} />
                    </div>
                  </div>
                  <div className="rights-item">
                    <div className="left">
                      <div className="title">{translate('plan_modal.dark_mode')}</div>
                      <div className="desc gradient">
                        {translate('plan_modal.no_points_consumption')}
                        <Popover
                          content={
                            <div className="common-popover w100">
                              <div>
                                {translate('plan_modal.dark_mode_faq')}
                                <a
                                  target="_blank"
                                  href="https://lingverse.feishu.cn/docx/VxnydIE4ao5NCyx7fnycqPX7npd#share-Evkidf4qYoA4AfxzoZJclzRkndh"
                                  rel="noreferrer"
                                  style={{ paddingLeft: 2, textDecoration: 'underline' }}
                                >
                                  {translate('plan_modal.faq_section')}
                                </a>
                              </div>
                            </div>
                          }
                          placement="bottom"
                        >
                          <ExclamationCircleOutlined />
                        </Popover>
                      </div>
                    </div>
                    <div className="right">
                      <img src={IconChecked} />
                    </div>
                  </div>
                  <ul>
                    <li>{translate('plan_modal.dark_mode_note_1')}</li>
                    <li>{translate('plan_modal.dark_mode_note_2')}</li>
                    <li>{translate('plan_modal.dark_mode_note_3')}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="plan-list">
            <div className="list">
              <div className="title">{translate('plan_modal.enterprise_oem')}</div>
              <div className="contact">
                <div className="left">
                  <img src={Code} />
                </div>
                <div className="right">
                  <div className="desc-t">{translate('plan_modal.oem_solution')}</div>
                  <div className="text">{translate('plan_modal.contact_sales')}</div>
                </div>
              </div>
              <Tooltip title={translate('plan_modal.scan_qr_contact')} trigger={['click']}>
                <Button type="primary">{translate('plan_modal.contact_sales')}</Button>
              </Tooltip>
              <div className="vip-rights">
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.more_member_benefits')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.oem_solution')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.online_support')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
              </div>
            </div>

            <div className="list actived">
              <div className="title">{translate('plan_modal.enterprise_api')}</div>
              <div className="price">
                <div className="discount-price">
                  <label className="num">{getDiscountPrice(companyProduct)}</label>
                  <label className="unit">
                    /{type === 'month' ? translate('plan_modal.month') : translate('plan_modal.year')}
                  </label>
                </div>
                {getOriginPrice(companyProduct)}
              </div>

              <div className="vip-company-desc">
                <div>{translate('plan_modal.no_points_included')}</div>
                <div>{translate('plan_modal.recharge_as_needed')}</div>
              </div>
              {companyProduct?.status === 1 && (
                <CheckLogin onLoginSuccess={getProductList}>
                  <Button
                    type="primary"
                    onClick={() => {
                      eventTracking('BuyNow')
                      onSubCompany()
                    }}
                  >
                    {translate('plan_modal.buy_now')}
                  </Button>
                </CheckLogin>
              )}
              {companyProduct?.status === 2 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('UpgradeNow')
                    onSubCompany()
                  }}
                >
                  {translate('plan_modal.upgrade_now')}
                  <div className="tips" onClick={upgradeTip}>
                    {translate('plan_modal.upgrade_rules')} <InfoCircleOutlined />
                  </div>
                </Button>
              )}
              {companyProduct?.status === 3 && (
                <Button
                  type="primary"
                  onClick={() => {
                    eventTracking('RenewNow')
                    onSubCompany()
                  }}
                >
                  {translate('plan_modal.renew_now')}
                </Button>
              )}
              {companyProduct?.status === 4 && <Button disabled>{translate('plan_modal.no_downgrade')}</Button>}
              <div className="vip-rights">
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.includes_all_black_rights')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.more_api_benefits')}</div>
                    <div className="desc">
                      <span
                        onClick={() => {
                          window.open('https://api.hifly.cc/hifly.html')
                        }}
                        className="border gradient"
                      >
                        {translate('plan_modal.image_digital_humans')}
                      </span>
                    </div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.points_discount')}</div>
                    <div className="desc gradient">{translate('plan_modal.up_to_50_off')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.generation_speed')}</div>
                    <div className="desc gradient">{translate('plan_modal.faster')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.dedicated_line')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>

                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.online_support')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
              </div>
            </div>

            <div className="list">
              <div className="title">{translate('plan_modal.custom_requirements')}</div>
              <div className="contact">
                <div className="left">
                  <img src={Code} />
                </div>
                <div className="right">
                  <div className="desc">
                    <label className="desc-t">{translate('plan_modal.enterprise_customization')}</label>
                  </div>
                  <div className="text">{translate('plan_modal.contact_sales')}</div>
                </div>
              </div>
              <Tooltip title={translate('plan_modal.scan_qr_contact')} trigger={['click']}>
                <Button type="primary">{translate('plan_modal.contact_sales')}</Button>
              </Tooltip>
              <div className="vip-rights">
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.more_member_benefits')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.custom_solutions')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
                <div className="rights-item">
                  <div className="left">
                    <div className="title">{translate('plan_modal.online_support')}</div>
                  </div>
                  <div className="right">
                    <img src={IconChecked} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <PayModal open={payModalOpen} onCancel={() => setPayModalOpen(false)} product={payProduct} />

      <ContractModal
        open={contractModalOpen}
        onCancel={() => setContractModalOpen(false)}
        product={companyProduct}
        onSuccess={() => setPayModalOpen(true)}
      />
    </>
  )
})

PlanProduct.displayName = 'PlanProduct'

export default PlanProduct
